import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Lock, ArrowRight, Bell, Shield, BarChart3, FileText,
  Calendar, ChevronRight, Building2
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';


const PortalPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 25 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  // Portal features
  const features = [
    { icon: BarChart3, title: 'Investment Dashboard', description: 'Track your investments, distributions, and portfolio performance in real-time' },
    { icon: FileText, title: 'Document Center', description: 'Access K-1 tax documents, quarterly reports, and investment agreements' },
    { icon: Shield, title: 'Secure Access', description: 'Bank-level encryption protects your sensitive financial information' },
    { icon: Bell, title: 'Notifications', description: 'Receive updates on distributions, new opportunities, and fund communications' }
  ];

  return (
    <>
      <Helmet>
        <title>Investor Portal | Coming Soon | ASAP Capital</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta 
          name="description" 
          content="ASAP Capital investor portal - secure access to your investment dashboard, documents, and performance reports. Coming soon." 
        />
        <link rel="canonical" href="/portal" />
      </Helmet>

      <div className="min-h-screen bg-[#0A1628]">
        <Header />
        
        <main>
          {/* Hero */}
          <section className="relative pt-36 pb-20 lg:pt-48 lg:pb-28 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#0A1628] via-[#0d1a2d] to-[#0A1628]" />
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[#D4AF37]/5 rounded-full blur-[200px] pointer-events-none" />
            
            <div className="relative z-10 max-w-[900px] mx-auto px-6 lg:px-12 text-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={containerVariants}
              >
                {/* Icon */}
                <motion.div 
                  className="w-24 h-24 rounded-3xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-8 border border-[#D4AF37]/20"
                  variants={itemVariants}
                >
                  <Lock className="h-12 w-12 text-[#D4AF37]" />
                </motion.div>

                {/* Badge */}
                <motion.div 
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 bg-amber-500/10 border border-amber-500/20"
                  variants={itemVariants}
                >
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-400"></span>
                  </span>
                  <span className="text-xs font-semibold text-amber-400 uppercase tracking-wider">Coming Soon</span>
                </motion.div>

                {/* Title */}
                <motion.h1 
                  className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] mb-6"
                  variants={itemVariants}
                >
                  Investor{' '}
                  <span className="bg-gradient-to-r from-[#D4AF37] via-[#F5D76E] to-[#D4AF37] bg-clip-text text-transparent">
                    Portal
                  </span>
                </motion.h1>

                {/* Description */}
                <motion.p 
                  className="text-lg text-white/60 max-w-2xl mx-auto mb-10"
                  variants={itemVariants}
                >
                  We're building a secure, dedicated platform for our investors to access 
                  investment dashboards, important documents, and real-time portfolio insights. 
                  The Investor Portal is currently under development.
                </motion.p>

                {/* CTA */}
                <motion.div 
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                  variants={itemVariants}
                >
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-12 rounded-full">
                      Get Notified When Live
                      <Bell className="h-4 w-4" />
                    </button>
                  </Link>
                  <Link to="/contact">
                    <button className="cta-contact px-8 py-3 h-12 rounded-full">
                      Schedule Consultation
                      <Calendar className="h-4 w-4" />
                    </button>
                  </Link>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* Features Preview */}
          <section className="py-16 lg:py-20 relative bg-gradient-to-b from-[#0d1a2d] to-[#0A1628]">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center mb-12"
              >
                <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4">What to Expect</h2>
                <p className="text-white/50 max-w-xl mx-auto">
                  The Investor Portal will provide comprehensive tools for managing your 
                  real estate investment portfolio.
                </p>
              </motion.div>

              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {features.map((feature, index) => (
                  <motion.div
                    key={feature.title}
                    className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-xl p-6 border border-white/10"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 border border-[#D4AF37]/20">
                      <feature.icon className="h-6 w-6 text-[#D4AF37]" />
                    </div>
                    <h3 className="text-white font-semibold mb-2">{feature.title}</h3>
                    <p className="text-white/50 text-sm">{feature.description}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Existing Investor CTA */}
          <section className="py-16 lg:py-20 relative">
            <div className="max-w-[800px] mx-auto px-6 lg:px-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-[#111D35] to-[#0A1628] rounded-2xl p-8 lg:p-10 border border-white/10 text-center"
              >
                <Building2 className="h-10 w-10 text-[#D4AF37] mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-4">Existing Investor?</h2>
                <p className="text-white/50 mb-6 max-w-lg mx-auto">
                  If you're a current ASAP Capital investor and need access to documents 
                  or have questions about your investment, please contact our investor 
                  relations team directly.
                </p>
                <Link to="/contact">
                  <button className="cta-contact px-8 py-3 h-12 rounded-full">
                    Contact Investor Relations
                    <ArrowRight className="h-4 w-4" />
                  </button>
                </Link>
              </motion.div>
            </div>
          </section>

          {/* Quick Links */}
          <section className="py-8 border-t border-white/5">
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <Link to="/track-record" className="text-white/50 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
                  View Track Record <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to="/fund-terms" className="text-white/50 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
                  Fund Terms <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to="/resources" className="text-white/50 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
                  Investor Resources <ChevronRight className="h-4 w-4" />
                </Link>
                <Link to="/faq" className="text-white/50 hover:text-[#D4AF37] transition-colors flex items-center gap-1">
                  FAQs <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default PortalPage;
